import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import type Guid from "_shared/guids/guid";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import RecordAccessPanel from "permissions/_views/records/record-access-panel";
import RecordLoadFailure from "permissions/_views/records/record-load-failure";
import { RecordCaption, recordKindOf, recordTitle } from "permissions/_views/records/record-caption";
import {
  type RecordDomain,
  type RecordPermissionsFrom,
  type RecordPermissionsState,
  recordPermissionsPath,
} from "permissions/_views/records/record-permissions-path";
import useRecordGrants from "permissions/_views/records/use-record-grants";
import { recordPage } from "permissions/_views/wording/permission-wording";
import useSessionUser from "permissions/session/use-session-user";
import { type ReactNode, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router";

/**
 * "Who has access", from a record's own screen: the record page's rows, Check access and Add people in
 * a drawer over the screen, with "Open full page" (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`,
 * "Entry points"). One component for every screen that links here, so each carries where the reader
 * came from the same way, and the full page can send them back.
 *
 * Shown only to a reader with Member Permissions; the caller decides that, as it decides where the
 * link sits.
 */

function DrawerBody(
  props: Readonly<{ domain: RecordDomain; recordId: Guid; kind: PermissionKind; kinds: readonly PermissionKind[]; from: RecordPermissionsFrom; onClose: () => void }>,
) {
  const { kind } = props;
  const noun = kind.targetNoun ?? "record";
  const [pageIndex, setPageIndex] = useState(0);
  const grants = useRecordGrants(kind, props.recordId, pageIndex);
  const state: RecordPermissionsState = { from: props.from };

  return (
    <Box sx={{ display: "grid", gap: 2, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary" component="p">
            {recordPage.whoHasAccess}
          </Typography>
          <Typography variant="h6" component="h2" id="who-has-access-title" sx={{ overflowWrap: "anywhere" }}>
            {recordTitle(grants.target, noun)}
          </Typography>
        </Box>
        <IconButton aria-label={recordPage.close} onClick={props.onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
          <RecordCaption domain={props.domain} grants={grants} noun={noun} />
        </Box>
        <Button
          component={RouterLink}
          to={recordPermissionsPath(props.domain, props.recordId)}
          state={state}
          size="small"
          startIcon={<OpenInFullIcon />}
          onClick={props.onClose}
        >
          {recordPage.openFullPage}
        </Button>
      </Box>
      <RecordAccessPanel
        kind={kind}
        kinds={props.kinds}
        recordId={props.recordId}
        recordName={grants.target.state === "found" ? grants.target.name : undefined}
        grants={grants}
        pageIndex={pageIndex}
        onPageChange={setPageIndex}
        idPrefix="who-has-access"
      />
    </Box>
  );
}

export function WhoHasAccessDrawer(
  props: Readonly<{ open: boolean; onClose: () => void; domain: RecordDomain; recordId: Guid; from: RecordPermissionsFrom }>,
) {
  const kindsQuery = useGetPermissionKindsQuery(undefined, { skip: !props.open });
  const kind = kindsQuery.data ? recordKindOf(kindsQuery.data.kinds, props.domain) : undefined;

  let body;
  if (kindsQuery.error) {
    body = (
      <Box sx={{ m: 2 }}>
        <RecordLoadFailure
          title={recordPage.kindsFailed}
          detail={getErrorMessageFromUnknown(kindsQuery.error)}
          onRetry={() => void kindsQuery.refetch()}
        />
      </Box>
    );
  } else if (!kindsQuery.data || !kind) {
    body = <Skeleton variant="rectangular" height={160} sx={{ m: 2 }} />;
  } else {
    body = (
      <DrawerBody domain={props.domain} recordId={props.recordId} kind={kind} kinds={kindsQuery.data.kinds} from={props.from} onClose={props.onClose} />
    );
  }

  return (
    <Drawer
      anchor="right"
      open={props.open}
      onClose={props.onClose}
      // On the modal layer: a conversation often opens in a dialog, and a drawer's own layer is below
      // dialogs. At the same layer, what opened last is on top: the drawer over the conversation's
      // dialog, and Add people, menus and pickers opened from the drawer over the drawer.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": "who-has-access-title",
          sx: { width: { xs: "100%", sm: 560, md: 720 }, maxWidth: "100%" },
        },
      }}
    >
      {props.open && body}
    </Drawer>
  );
}

export type WhoHasAccessEntry = Readonly<{
  /** Whether the reader may see who has access: Member Permissions. An entry is drawn only when this is true. */
  mayOpen: boolean;
  /** Opens the drawer on one record. */
  open: (recordId: Guid) => void;
  /** The drawer, to render once beside the entry. */
  drawer: ReactNode;
}>;

/**
 * The "Who has access" entry on a record's own screen: whether to show it, what it opens, and the
 * drawer it opens. Each screen draws its own control — a row action, a header button, a menu item —
 * and every one opens the same drawer and carries where the reader came from the same way.
 *
 * `label` names the screen for the full page's link back, for example "the proposal".
 */
export function useWhoHasAccess(domain: RecordDomain, label: string): WhoHasAccessEntry {
  const sessionUser = useSessionUser();
  const location = useLocation();
  const [recordId, setRecordId] = useState<Guid | undefined>();
  const mayOpen = sessionUser?.canReadMemberPermissions() ?? false;
  const drawer =
    mayOpen && recordId ? (
      <WhoHasAccessDrawer
        open
        onClose={() => setRecordId(undefined)}
        domain={domain}
        recordId={recordId}
        from={{ pathname: location.pathname, search: location.search, label }}
      />
    ) : null;
  return { mayOpen, open: setRecordId, drawer };
}
